import { ApiService } from '../../../services/apiService';
import { ProfileAnalysesListResponse, SingleProfileAnalysisResponse } from '../types/document.types';

// Global Talent Visa API endpoints
const GTV_ENDPOINTS = {
    CHAT: '/api/domain/global_talent_visa/chat/message',
    UPLOAD: '/api/domain/global_talent_visa/upload',
    QUERY: '/api/domain/global_talent_visa/query',
    INFO: '/api/domain/global_talent_visa/info',
    CLEAR: '/api/domain/global_talent_visa/clear',
    PERSONAL_STATEMENT: '/api/domain/global_talent_visa/personal-statement/generate',
    PROFILE_ANALYSIS: '/api/domain/global_talent_visa/profile/analyze',
    PROFILE_ANALYSES: '/api/domain/global_talent_visa/profile/analyses',
    PROFILE_ANALYSIS_BY_ID: (id: string) => `/api/domain/global_talent_visa/profile/analyses/${id}`,
};

// Interface for chat message request
export interface GTVChatRequest {
    message: string;
    previous_response_id?: string;
    chat_id?: string;
}

// Interface for chat message response
export interface GTVChatResponse {
    response: string;
    response_id: string;
    status: 'success' | 'error';
    user?: {
        uid: string;
        email: string;
    };
}

// Interface for document query request
export interface GTVQueryRequest {
    query: string;
    custom_prompt?: string;
    search_kwargs?: {
        k?: number;
        fetch_k?: number;
    };
}

// Interface for document query response
export interface GTVQueryResponse {
    answer: string;
    source_documents: Array<{
        page_content: string;
        metadata: {
            source: string;
            page: number;
        };
    }>;
    status: 'success' | 'error';
}

// Interface for document info response
export interface GTVDocumentInfo {
    document_info: {
        total_documents: number;
        total_chunks: number;
        collection_name: string;
    };
    status: 'success' | 'error';
}

// Interface for personal statement generation request
export interface GTVPersonalStatementRequest {
    custom_prompt?: string;
}

// Interface for personal statement generation response (file download)
export interface GTVPersonalStatementResponse {
    blob: Blob;
    filename: string;
}

// Interface for profile analysis request
export interface GTVProfileAnalysisRequest {
    category?: 'digital_technology' | 'academia_research' | 'arts_culture';
}

// Interface for profile analysis response
export interface GTVProfileAnalysisResponse {
    analysis: string;
    category?: string;
    user_has_documents: boolean;
    document_count: {
        cv: number;
        linkedin: number;
        additional: number;
    };
    linkedin_provided: boolean;
    analysis_timestamp?: string;
    status: 'success' | 'error';
}

// Global Talent Visa Service class extending the centralized API service
class GTVService extends ApiService {
    // Send a chat message to the GTV AI assistant
    async sendChatMessage(request: GTVChatRequest): Promise<GTVChatResponse> {
        return this.post<GTVChatResponse>(GTV_ENDPOINTS.CHAT, request);
    }

    // Upload and process GTV documents
    async uploadDocuments(): Promise<GTVDocumentInfo> {
        return this.post<GTVDocumentInfo>(GTV_ENDPOINTS.UPLOAD);
    }

    // Query GTV documents
    async queryDocuments(request: GTVQueryRequest): Promise<GTVQueryResponse> {
        return this.post<GTVQueryResponse>(GTV_ENDPOINTS.QUERY, request);
    }

    // Get GTV document information
    async getDocumentInfo(): Promise<GTVDocumentInfo> {
        return this.get<GTVDocumentInfo>(GTV_ENDPOINTS.INFO);
    }

    // Clear GTV vector database
    async clearDatabase(): Promise<{ message: string; status: string }> {
        return this.post<{ message: string; status: string }>(GTV_ENDPOINTS.CLEAR);
    }

    // Generate personal statement template as DOCX file
    async generatePersonalStatement(request: GTVPersonalStatementRequest = {}): Promise<GTVPersonalStatementResponse> {
        return this.downloadFile(GTV_ENDPOINTS.PERSONAL_STATEMENT, request);
    }

    // Analyze profile for GTV eligibility
    async analyzeProfile(request: GTVProfileAnalysisRequest = {}): Promise<GTVProfileAnalysisResponse> {
        return this.post<GTVProfileAnalysisResponse>(GTV_ENDPOINTS.PROFILE_ANALYSIS, request);
    }

    // Get past profile analyses for the user
    async getProfileAnalyses(limit?: number): Promise<ProfileAnalysesListResponse> {
        const endpoint = limit 
            ? `${GTV_ENDPOINTS.PROFILE_ANALYSES}?limit=${limit}`
            : GTV_ENDPOINTS.PROFILE_ANALYSES;
        return this.get<ProfileAnalysesListResponse>(endpoint);
    }

    // Get a specific profile analysis by ID
    async getProfileAnalysis(analysisId: string): Promise<SingleProfileAnalysisResponse> {
        return this.get<SingleProfileAnalysisResponse>(GTV_ENDPOINTS.PROFILE_ANALYSIS_BY_ID(analysisId));
    }

    // Delete a specific profile analysis by ID
    async deleteProfileAnalysis(analysisId: string): Promise<{ message: string; status: string }> {
        return this.delete<{ message: string; status: string }>(GTV_ENDPOINTS.PROFILE_ANALYSIS_BY_ID(analysisId));
    }
}

// Create and export a singleton instance
export const gtvService = new GTVService();

// Export individual functions for backward compatibility
export const sendGTVChatMessage = (request: GTVChatRequest): Promise<GTVChatResponse> =>
    gtvService.sendChatMessage(request);

export const uploadGTVDocuments = (): Promise<GTVDocumentInfo> =>
    gtvService.uploadDocuments();

export const queryGTVDocuments = (request: GTVQueryRequest): Promise<GTVQueryResponse> =>
    gtvService.queryDocuments(request);

export const getGTVDocumentInfo = (): Promise<GTVDocumentInfo> =>
    gtvService.getDocumentInfo();

export const clearGTVDatabase = (): Promise<{ message: string; status: string }> =>
    gtvService.clearDatabase();

export const generateGTVPersonalStatement = (request: GTVPersonalStatementRequest = {}): Promise<GTVPersonalStatementResponse> =>
    gtvService.generatePersonalStatement(request); 

export const analyzeGTVProfile = (request: GTVProfileAnalysisRequest = {}): Promise<GTVProfileAnalysisResponse> =>
    gtvService.analyzeProfile(request);

export const getGTVProfileAnalyses = (limit?: number): Promise<ProfileAnalysesListResponse> =>
    gtvService.getProfileAnalyses(limit);

export const getGTVProfileAnalysis = (analysisId: string): Promise<SingleProfileAnalysisResponse> =>
    gtvService.getProfileAnalysis(analysisId);

export const deleteGTVProfileAnalysis = (analysisId: string): Promise<{ message: string; status: string }> =>
    gtvService.deleteProfileAnalysis(analysisId); 